/**
 * ``/workspace/reports`` index — Voice of X hub landing.
 *
 * Lightweight wrapper: surfaces the ``VoiceReportsList`` component +
 * "Nuovo report" CTA, navigates to the detail page on row click. The
 * actual list UI lives in
 * ``features/voice_report/components/VoiceReportsList.tsx`` so the route
 * stays a trivial file (same convention as
 * ``workspace/projects/index.tsx`` / ``workspace/templates/index.tsx``).
 *
 * NewReportDialog wiring lands in M2.3; until then the empty-state CTA
 * is wired to a placeholder ``onCreate`` that opens the route TODO.
 */

import { Trans } from "@lingui/react/macro";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import * as React from "react";

import { cn } from "@ando/ui";

import { VoiceReportsList } from "@/features/voice_report";
import { voiceReportsListQueryOptions } from "@/features/voice_report/hooks";

export const Route = createFileRoute("/_authed/workspace/reports/")({
  // Warm the default voice-reports page (limit 50 / offset 0) on hover/focus.
  loader: ({ context: { queryClient } }) => {
    void queryClient.ensureQueryData(voiceReportsListQueryOptions());
  },
  component: VoiceReportsHubPage,
});

function VoiceReportsHubPage(): React.ReactElement {
  const navigate = useNavigate();

  const handleSelect = React.useCallback(
    (reportId: string) => {
      void navigate({
        to: "/workspace/reports/$reportId",
        params: { reportId },
      });
    },
    [navigate],
  );

  // M2.3 ships ``NewReportDialog`` — until then the CTA is hidden from
  // the in-page header (the empty-state CTA inside ``VoiceReportsList``
  // also disables itself when ``onCreate`` is undefined).
  const handleCreate = undefined;

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-4 py-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display font-semibold text-[22px] text-ink-1 leading-tight tracking-tight">
            <Trans>Voice of X</Trans>
          </h1>
          <p className="mt-1 max-w-2xl text-[13px] text-ink-3 leading-relaxed">
            <Trans>
              Report citation-grounded che sintetizzano l'opinione di clienti, prospect
              o utenti a partire dalla tua knowledge.
            </Trans>
          </p>
        </div>
        {handleCreate ? (
          <button
            type="button"
            onClick={handleCreate}
            className={cn(
              "inline-flex h-10 items-center gap-2 rounded-full bg-ink-1 px-4 font-medium text-[13px] text-cream",
              "transition-colors hover:bg-ink-2",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-2 focus-visible:ring-offset-cream",
            )}
          >
            <Plus size={14} strokeWidth={2} />
            <Trans>Nuovo report</Trans>
          </button>
        ) : null}
      </header>

      <VoiceReportsList onSelect={handleSelect} onCreate={handleCreate} />
    </div>
  );
}
